import { mediaCommentCountChangedEvent } from '@lib/events/eventBus';
import { compareByAnchor, MediaCommentAnchor } from '@lib/media/commentAnchor';
import { DataFrame } from '@lib/mobx/dataframe';
import { getAvatarInitial, getColorFromString } from '@lib/utils/colorUtils';
import { userStore } from '@stores/User';
import { computed, makeObservable, runInAction } from 'mobx';

/**
 * How long a comment or a reply may be.
 *
 * Mirrors the API's `BODY_MAX_LENGTH`, which is the authority — this copy exists so the
 * textarea can stop at the limit instead of letting someone write a paragraph the POST then
 * refuses. The cap on how MANY comments a media holds is deliberately not mirrored: it is
 * reachable only at 100 comments on one media, and a second copy of a number nobody hits is a
 * number that silently goes stale. That one is handled where it actually arrives, as a 422.
 */
export const COMMENT_BODY_MAX_LENGTH = 4000;

// ── Wire ───────────────────────────────────────────────────────

/**
 * The author block as the API sends it. `null` when the account no longer resolves (deleted),
 * and every field inside is optional — an SSO sign-up that never gave a name has neither.
 */
type UserIdentityWire = {
  userId: string;
  email?: string;
  name?: { first?: string; last?: string; nickname?: string };
} | null;

type MediaCommentReplyWire = {
  _id: string;
  authorId: string;
  author: UserIdentityWire;
  body: string;
  createdAt: number;
  updatedAt: number;
};

type MediaCommentWire = MediaCommentReplyWire & {
  libraryId: string;
  mediaId: string;
  anchor: MediaCommentAnchor;
  replies: MediaCommentReplyWire[];
};

// ── Owned shapes ───────────────────────────────────────────────

/**
 * Who wrote something, reduced to what the modal shows.
 *
 * The email is dropped here on purpose. The API hands it to everyone who can read the media —
 * its own comment notes that this happens on exactly the libraries several accounts can hold at
 * once — and the modal has no reason to be the surface that prints a colleague's address back
 * at them. `displayName` and the avatar are derived from it (see `toAuthor`), which identifies
 * a person without publishing how to contact them.
 */
export type MediaCommentAuthor = {
  /** Compare against `userStore.data?._id` to decide whether this is the reader's own writing. */
  userId: string;
  displayName: string;
  /** One character for the avatar; empty when nothing in the name yields one. */
  initial: string;
  /** A CSS colour for the avatar's background. */
  avatarColor: string;
};

export type MediaCommentReply = {
  id: string;
  author: MediaCommentAuthor;
  body: string;
  /** Unix ms. */
  createdAt: number;
  /** Unix ms; later than `createdAt` once the author edited it. */
  updatedAt: number;
};

export type MediaComment = MediaCommentReply & {
  anchor: MediaCommentAnchor;
  /** In creation order; empty when nobody has answered. A reply has no replies of its own. */
  replies: MediaCommentReply[];
};

/**
 * What to call someone, in the order of how much they chose it: the nickname they set, the name
 * their account carries, then the part of their email before the `@`.
 *
 * "Someone" rather than "Unknown" for the last case, which covers both a deleted account and an
 * SSO sign-up with no name at all: the comment demonstrably has an author, so the line should
 * read as one person we cannot name rather than as missing data.
 */
const displayNameFor = (identity: UserIdentityWire): string => {
  const nickname = identity?.name?.nickname?.trim();
  if (nickname) return nickname;
  const full = [identity?.name?.first, identity?.name?.last]
    .filter((part) => part?.trim())
    .join(' ')
    .trim();
  if (full) return full;
  const localPart = identity?.email?.split('@')[0]?.trim();
  if (localPart) return localPart;
  return 'Someone';
};

const toAuthor = (authorId: string, identity: UserIdentityWire): MediaCommentAuthor => ({
  // `authorId` rather than `identity.userId`: the id is always sent, the block may be null.
  userId: authorId,
  displayName: displayNameFor(identity),
  // The same two helpers, fed the same two fields, as the account's own avatar (`ProfileAvatar`
  // in `UserMenu`), so a person's circle looks the same in a comment as it does in the sidebar.
  // Hashing anything else — this used to hash the user id — gives the same person a different
  // colour in two places on one screen. Both are derived here and the email goes no further.
  initial: getAvatarInitial(identity),
  avatarColor: getColorFromString(identity?.email ?? ''),
});

const toReply = (wire: MediaCommentReplyWire): MediaCommentReply => ({
  id: wire._id,
  author: toAuthor(wire.authorId, wire.author),
  body: wire.body,
  createdAt: wire.createdAt,
  updatedAt: wire.updatedAt,
});

const toComment = (wire: MediaCommentWire): MediaComment => ({
  ...toReply(wire),
  anchor: wire.anchor,
  replies: (wire.replies ?? []).map(toReply),
});

// ── Store ──────────────────────────────────────────────────────

/**
 * The comments on one media, and the writes that change them.
 *
 * One instance per media the modal has open, not a singleton: the facts are per-media, and a
 * shared store would have to be told which media it is describing on every read — which is the
 * bug where a marker from the previous media survives on the next one's timeline.
 *
 * Mutations send the change and fold the object the API returns back into `data`, rather than
 * refetching the list. The API answers every write with the resulting comment or reply, so a
 * refetch would be a second round trip for something already in hand — and re-reading through
 * `DataFrame` would blank `data` on a transient failure, taking the markers and the count with
 * it. Nothing is applied optimistically: a note that appears and then vanishes is worse than
 * one that takes a moment to appear.
 */
export class MediaCommentsStore extends DataFrame<MediaComment[], MediaCommentWire[]> {
  constructor(
    readonly libraryId: string,
    readonly mediaId: string,
  ) {
    super();
    makeObservable(this);
  }

  private get basePath() {
    return `/library/${this.libraryId}/media/${this.mediaId}/comments`;
  }

  protected fetch(abortSignal: AbortSignal) {
    return userStore.apiWithAuth<MediaCommentWire[]>(this.basePath, { signal: abortSignal });
  }

  protected override postProcess(wire: MediaCommentWire[]): MediaComment[] {
    return wire.map(toComment).sort(compareByAnchor);
  }

  /**
   * How many comments to advertise on the tab.
   *
   * Zero both before the first read and when the media genuinely has none — `data` is null
   * until a fetch succeeds, and a failed one puts it back. That is the right reading for a
   * badge, which should say nothing rather than claim a count it does not have, but it is the
   * wrong one for an empty state: see `CommentsPanel`, which keys "No comments yet" on `data`
   * itself rather than on this.
   */
  @computed
  get count(): number {
    return this.data?.length ?? 0;
  }

  /**
   * A delta rather than `count`: `data` is null whenever the list failed to load, yet the write
   * still landed, so only the change is known for certain.
   */
  private announceCountChange(delta: 1 | -1) {
    mediaCommentCountChangedEvent.emit({ libraryId: this.libraryId, mediaId: this.mediaId, delta });
  }

  /** Replaces one comment in place, keeping the list in timeline order. */
  private applyComment(comment: MediaComment) {
    runInAction(() => {
      const without = (this.data ?? []).filter((existing) => existing.id !== comment.id);
      this.data = [...without, comment].sort(compareByAnchor);
    });
  }

  /** Applies a change to one comment's replies, leaving the rest of the list alone. */
  private applyReplies(
    commentId: string,
    change: (replies: MediaCommentReply[]) => MediaCommentReply[],
  ) {
    runInAction(() => {
      this.data =
        this.data?.map((comment) =>
          comment.id === commentId ? { ...comment, replies: change(comment.replies) } : comment,
        ) ?? null;
    });
  }

  async create(body: string, anchor: MediaCommentAnchor): Promise<MediaComment> {
    const wire = await userStore.apiWithAuth<MediaCommentWire>(this.basePath, {
      method: 'POST',
      body: { body, anchor },
    });
    const comment = toComment(wire);
    this.applyComment(comment);
    this.announceCountChange(1);
    return comment;
  }

  /** What it says, where it points, or both. The API accepts only the author's own. */
  async edit(
    commentId: string,
    patch: { body?: string; anchor?: MediaCommentAnchor },
  ): Promise<void> {
    const wire = await userStore.apiWithAuth<MediaCommentWire>(`${this.basePath}/${commentId}`, {
      method: 'PATCH',
      body: patch,
    });
    this.applyComment(toComment(wire));
  }

  /**
   * The comment and the thread under it. The API cascades the replies — a reply carries no
   * anchor of its own, so it has nowhere on the timeline to outlive its parent — which is why
   * the caller warns before doing this to a comment that has any.
   */
  async remove(commentId: string): Promise<void> {
    await userStore.apiWithAuth(`${this.basePath}/${commentId}`, { method: 'DELETE' });
    runInAction(() => {
      this.data = this.data?.filter((comment) => comment.id !== commentId) ?? null;
    });
    this.announceCountChange(-1);
  }

  async reply(commentId: string, body: string): Promise<void> {
    const wire = await userStore.apiWithAuth<MediaCommentReplyWire>(
      `${this.basePath}/${commentId}/replies`,
      { method: 'POST', body: { body } },
    );
    const reply = toReply(wire);
    this.applyReplies(commentId, (replies) => [...replies, reply]);
  }

  async editReply(commentId: string, replyId: string, body: string): Promise<void> {
    const wire = await userStore.apiWithAuth<MediaCommentReplyWire>(
      `${this.basePath}/${commentId}/replies/${replyId}`,
      { method: 'PATCH', body: { body } },
    );
    const edited = toReply(wire);
    this.applyReplies(commentId, (replies) =>
      replies.map((reply) => (reply.id === edited.id ? edited : reply)),
    );
  }

  async removeReply(commentId: string, replyId: string): Promise<void> {
    await userStore.apiWithAuth(`${this.basePath}/${commentId}/replies/${replyId}`, {
      method: 'DELETE',
    });
    this.applyReplies(commentId, (replies) => replies.filter((reply) => reply.id !== replyId));
  }
}
